import React, { useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Stepper from "../../components/ui/Stepper/Stepper.jsx";
import TicketPanel from "../../components/cards/TicketPanel/TicketPanel.jsx";
import ReviewStep from "./steps/ReviewStep.jsx";
import { useApp } from "../../hooks/useApp.js";
import { buildSteps } from "./stepConfig.js";
import { getToken } from "../../services/api.js";
import { getReturnForBusiness, createFreshReturnForBusiness, getReturnById } from "../../services/trucksService.js";
import { getBusiness } from "../../services/businessService.js";

const Wizard = () => {
  const { step, returnId: viewReturnId } = useParams();
  const navigate = useNavigate();
  const { state, dispatch, showToast } = useApp();

  // Rehydrate isTaxPreparer from the stored login session on a hard
  // refresh / direct nav — context state resets on reload, sessionStorage
  // doesn't. Only runs if the reducer hasn't already been told (avoids
  // clobbering a value just set by a fresh login in the same session).
  useEffect(() => {
    if (state.isTaxPreparer === null || state.isTaxPreparer === undefined) {
      try {
        const storedUser = sessionStorage.getItem("user");
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          dispatch({ type: "SET_IS_TAX_PREPARER", isTaxPreparer: !!parsed.taxPreparer });
        }
      } catch {
        // malformed/missing session — leave isTaxPreparer as-is
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reloads the full business record (name, EIN, address, taxPreparerId, ...)
  // from the backend whenever businessId changes, rather than trusting
  // whatever partial state.biz happens to already hold. Continuing an
  // existing filing (Dashboard/My Filings' "Continue") only seeds state.biz
  // with { name, ein } before landing here (see MyFilings.jsx/Dashboard.jsx's
  // openFiling) - without this, FormTypeStep's Tax Preparer dropdown (and any
  // other business field) would show blank until the user happened to reach
  // Review, the only step that used to do this fetch itself. Hoisted here -
  // the one mount point every wizard step (including the view-only Review
  // path, whose businessId is set async by the effect below) renders under -
  // so every step sees the complete business record, not just Review.
  useEffect(() => {
    if (!getToken() || !state.businessId) return;
    let cancelled = false;
    getBusiness(state.businessId)
      .then((saved) => {
        if (!cancelled) dispatch({ type: "SET_BIZ", patch: saved });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.businessId]);

  // The wizard's current step lives in the URL (/wizard/:step), so a hard
  // refresh can land directly on any step - Form, Amended Return, Weight
  // Increase, Mileage Exceeded, VIN Correction - without ever mounting
  // TrucksStep, the only other place that fetches the saved return.
  // Context state resets to blank on reload (only businessId/preparerId/
  // returnId survive, via sessionStorage), so without this, every field
  // those steps show (tax year, filing type, amendment reason/vehicles,
  // VIN-correction rows) would appear empty even though it's still saved
  // server-side. Fetching here, once per wizard mount regardless of which
  // step, guarantees it's always reloaded.
  // state.returnId is null only when this business hasn't had a return
  // attached yet this session - either a genuinely fresh filing or a page
  // reload before this effect has ever run. It's never null on a reload
  // *after* a return was already attached, since returnId (unlike
  // returnType) is mirrored to sessionStorage - see AppContext's
  // PERSISTED_ID_KEYS. So gating the fresh-draft request on "!state.returnId"
  // only ever fires once per new filing, never again on a later reload of
  // that same filing.
  //
  // Gated on state.returnType too (not just businessId): a fresh "New
  // Filing" lands directly on FormTypeStep, where Business and Filing type
  // are two fields on the SAME page with no fixed pick order - if this fired
  // as soon as Business alone was chosen, picking Business before the
  // Amended/VIN Correction card would reuse the business's current draft
  // (via the plain getReturnForBusiness branch below) instead of creating a
  // genuinely fresh one, and the amendment would then get saved onto
  // whatever unrelated return that reuse fetch happened to return. Waiting
  // for both fields removes the ordering dependency entirely - order-
  // independent from the filer's perspective, but load-bearing for
  // correctness.
  //
  // Reusing the business's existing draft is still fine (expected, even) for
  // a fresh Original/Final filing, and for "Continue filing" (Dashboard/
  // Businesses/MyFilings) - those set returnType and businessId together (via
  // one SET_RETURN/SET_BUSINESS_ID dispatch batch) before navigating here, so
  // state.returnTotals is already truthy by the time this effect runs and it
  // never re-fetches at all.
  //
  // BUT that returnType requirement must not apply once state.returnId is
  // already known (mirrored to sessionStorage, unlike returnType - see the
  // block above): a refresh on AmendedReturnStep/VinCorrectionStep/
  // WeightIncreaseStep resets state.returnType to blank same as everything
  // else, and without this OR clause this whole effect - the one thing that
  // would re-fetch and restore returnType - stayed gated off forever on that
  // very field being blank, a chicken-and-egg bug. buildSteps(state) then
  // built the plain Original-shaped sequence (no amendedReturn/vinCorrection
  // step inserted), so whatever position the URL still pointed at silently
  // resolved to "trucks" instead. Once returnId is already known, the
  // order-independent-picking scenario above can't apply (that's only ever
  // true before any return exists yet), so it's always safe - and necessary
  // - to fetch immediately.
  useEffect(() => {
    if (viewReturnId || !getToken() || !state.businessId || (!state.returnType && !state.returnId) || state.returnTotals) return;
    let cancelled = false;
    const wantsFreshDraft =
      !state.returnId &&
      (state.returnType === "amended" || state.returnType === "vincorrection" || state.forceFreshDraft);
    const fetchReturn = wantsFreshDraft
      ? createFreshReturnForBusiness(state.businessId)
      : getReturnForBusiness(state.businessId);
    fetchReturn
      .then((r) => {
        if (!cancelled) dispatch({ type: "SET_RETURN", ...r });
      })
      .catch((err) => {
        if (!cancelled) showToast(err.message || "Couldn't load your return. Please try again.");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewReturnId, state.businessId, state.returnType]);

  // My Filings' "View" action (/wizard/review/:returnId) - loads this exact
  // return_id's full detail, regardless of whether it's the business's
  // current draft, and regardless of status (submitted/accepted/rejected
  // returns are immutable but still viewable). Unlike the draft-reuse fetch
  // above, this never falls back to "whatever this business's current draft
  // is".
  useEffect(() => {
    if (!viewReturnId) return;
    let cancelled = false;
    getReturnById(viewReturnId)
      .then((r) => {
        if (cancelled) return;
        dispatch({ type: "SET_BUSINESS_ID", businessId: r.businessId });
        dispatch({ type: "SET_RETURN", ...r });
      })
      .catch((err) => {
        if (!cancelled) {
          showToast(err.message || "Couldn't load this return. Please try again.");
          navigate("/dashboard/filings");
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewReturnId]);

  // The step sequence itself is derived from state (filing type, amendment
  // reason, tax-preparer skip) - see stepConfig.js. Amended/VIN-correction
  // filers get extra dedicated pages inserted between Form and Trucks, which
  // pushes Trucks (and everything after it) to a later position; nothing
  // else about the numbering scheme changes.
  const steps = buildSteps(state);
  const n = Math.min(steps.length, Math.max(1, Number(step) || 1));
  const current = viewReturnId ? null : steps[n - 1];

  useEffect(() => {
    if (viewReturnId) return;
    dispatch({ type: "GO_STEP", step: n });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n, viewReturnId]);

  const goStep = (target) => navigate(`/wizard/${target}`);
  const goBack = () => {
    if (n === 1) {
      navigate("/dashboard");
    } else {
      goStep(n - 1);
    }
  };

  // ReviewStep's per-section "Edit" buttons - jump straight to any step by
  // its stepConfig.js key, rather than only ever going back one step at a
  // time. Business/Tax Preparer aren't part of this sequence at all (see
  // stepConfig.js - they're standalone dashboard pages), so those two get
  // their own handlers that navigate there directly and bring the caller
  // back to this exact Review step (via returnTo) once saved.
  const goToStepKey = (key) => {
    const idx = steps.findIndex((s) => s.key === key);
    if (idx >= 0) goStep(idx + 1);
  };
  const editBusiness = () => navigate("/dashboard/businesses/form", { state: { returnTo: `/wizard/${n}` } });
  const editPreparer = () => {
    // Whichever preparer Review is actually displaying - this return's own override if it
    // has one, else the business's default (same resolution as ReviewStep's effectivePreparerId).
    dispatch({ type: "SET_PREPARER_ID", preparerId: state.returnTaxPreparerId ?? state.biz.taxPreparerId ?? null });
    navigate("/dashboard/preparers/form", { state: { returnTo: `/wizard/${n}` } });
  };

  const StepComponent = current?.component ?? null;
  const showTicketPanel = !current?.hideTicketPanel;

  const gridCols = showTicketPanel ? "lg:grid-cols-[1fr_320px]" : "lg:grid-cols-[1fr]";

  // My Filings' "View" action - a read-only look at one specific past
  // return_id's full detail (business, trucks, credits, etc.), bypassing the
  // normal editable step sequence entirely: submitted/accepted/rejected
  // returns aren't draft-editable (see TaxReturn.isDraft() on the backend),
  // and even a draft return reached this way isn't necessarily "the"
  // business's current draft that the rest of the wizard would resume.
  if (viewReturnId) {
    return (
      <div className="max-w-[1500px] mx-auto px-[22px] py-[26px] pb-[70px]">
        <div className="text-[var(--muted)] text-[13px] mb-2">
          <Link to="/dashboard/filings" className="text-[var(--muted)] hover:text-[var(--accent)]">
            My Filings
          </Link>{" "}
          / Return #{viewReturnId}
        </div>

        <div className="grid gap-5 items-start lg:grid-cols-[1fr_320px]">
          <div>
            <ReviewStep viewOnly onBack={() => navigate("/dashboard/filings")} />
          </div>
          <TicketPanel />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1500px] mx-auto px-[22px] py-[26px] pb-[70px]">
      <div className="text-[var(--muted)] text-[13px] mb-2">
        <Link to="/dashboard" className="text-[var(--muted)] hover:text-[var(--accent)]">
          Dashboard
        </Link>{" "}
        / Form 2290 — TY2026
      </div>

      <Stepper steps={steps} n={n} onNavigate={goStep} />

      <div className={`grid gap-5 items-start ${gridCols}`}>
        <div>
          {StepComponent && (
            <StepComponent
              onNext={() => goStep(n + 1)}
              onBack={goBack}
              onDashboard={() => navigate("/dashboard")}
              onGoToStep={goToStepKey}
              onEditBusiness={editBusiness}
              onEditPreparer={editPreparer}
            />
          )}
        </div>

        {showTicketPanel && <TicketPanel />}
      </div>
    </div>
  );
};

export default Wizard;
